import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Paper, ButtonGroup, Button, Tooltip, Chip, Typography } from '@mui/material';
import {
  CropFree as RectangleIcon,
  RadioButtonUnchecked as CircleIcon,
  Timeline as PolygonIcon,
  LinearScale as LineIcon,
  PanTool as SelectIcon
} from '@mui/icons-material';

const MapCanvas = ({
  zones = [],
  entities = [],
  gateways = [],
  selectedZone,
  selectedGateway,
  onZoneSelect,
  onZoneCreate,
  onZoneUpdate,
  onGatewayClick,
  onGatewayPosition,
  drawingMode,
  onDrawingModeChange,
  showLayers = { zones: true, gateways: true, equipment: true, patients: true, staff: true, barriers: true },
  floorPlan = null,
  scale = 1,
  offset = { x: 0, y: 0 },
  isPositioningGateway = false
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [floorPlanImage, setFloorPlanImage] = useState(null);

  // Zone type colors
  const zoneColors = {
    room: { fill: '#4CAF50', stroke: '#2E7D32' },
    bed: { fill: '#2196F3', stroke: '#1565C0' },
    ward: { fill: '#FF9800', stroke: '#E65100' },
    department: { fill: '#9C27B0', stroke: '#6A1B9A' },
    restricted: { fill: '#F44336', stroke: '#C62828' },
    corridor: { fill: '#607D8B', stroke: '#37474F' },
    elevator: { fill: '#795548', stroke: '#4E342E' },
    exit: { fill: '#E91E63', stroke: '#AD1457' }
  };

  // Entity type colors
  const entityColors = {
    equipment: '#FF5722',
    patient: '#E91E63',
    staff: '#3F51B5'
  };

  // Drawing tools
  const drawingTools = [
    { mode: 'select', icon: SelectIcon, label: 'Selecionar' },
    { mode: 'rectangle', icon: RectangleIcon, label: 'Retângulo' },
    { mode: 'circle', icon: CircleIcon, label: 'Círculo' },
    { mode: 'polygon', icon: PolygonIcon, label: 'Polígono' },
    { mode: 'corridor', icon: LineIcon, label: 'Corredor' }
  ];

  // Canvas drawing function
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Save context
    ctx.save();
    
    // Apply transformations
    ctx.scale(scale, scale);
    ctx.translate(offset.x, offset.y);
    
    // Always draw grid first
    drawGrid(ctx, width, height);
    
    // Draw floor plan if available
    drawFloorPlan(ctx);
    
    // Draw zones
    if (showLayers.zones) {
      zones.forEach(zone => drawZone(ctx, zone));
    }
    
    // Draw entities
    console.log('MapCanvas: Drawing entities:', entities.length, entities);
    entities.forEach(entity => {
      console.log('Checking entity:', entity.entity_type, 'showLayers:', showLayers[entity.entity_type]);
      if (showLayers[entity.entity_type]) {
        drawEntity(ctx, entity);
      }
    });
    
    // Draw gateways
    if (showLayers.gateways) {
      console.log('MapCanvas: Drawing', gateways.length, 'gateways:', gateways);
      gateways.forEach(gateway => drawGateway(ctx, gateway));
    }
    
    // Draw current drawing path
    if (isDrawing && currentPath.length > 0) {
      drawCurrentPath(ctx);
    }
    
    // Restore context
    ctx.restore();
  }, [zones, entities, gateways, selectedZone, selectedGateway, isDrawing, currentPath, scale, offset, floorPlan, showLayers]);

  // Draw grid background
  const drawGrid = (ctx, width, height) => {
    const gridSize = 20;
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    
    // Draw background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, width, height);
    
    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // Horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // Load floor plan image
  useEffect(() => {
    if (floorPlan && floorPlan !== floorPlanImage?.src) {
      const img = new Image();
      img.onload = () => {
        setFloorPlanImage(img);
        drawCanvas();
      };
      img.crossOrigin = 'anonymous';
      img.src = floorPlan;
    }
  }, [floorPlan]);

  // Draw floor plan image
  const drawFloorPlan = (ctx) => {
    if (floorPlanImage) {
      const canvasWidth = ctx.canvas.width;
      const canvasHeight = ctx.canvas.height;
      const imgWidth = floorPlanImage.width;
      const imgHeight = floorPlanImage.height;
      
      // Calculate scale to fit image while maintaining aspect ratio
      const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;
      
      // Center the image
      const x = (canvasWidth - scaledWidth) / 2;
      const y = (canvasHeight - scaledHeight) / 2;
      
      ctx.globalAlpha = 0.8;
      ctx.drawImage(floorPlanImage, x, y, scaledWidth, scaledHeight);
      ctx.globalAlpha = 1;
    }
  };

  // Draw zone
  const drawZone = (ctx, zone) => {
    console.log('Drawing zone:', zone.zone_code, 'coordinates:', zone.coordinates);
    
    let coords = zone.coordinates;
    if (!coords) {
      console.log('No coordinates for zone:', zone.zone_code);
      return;
    }
    
    // Parse coordinates if they're a string
    if (typeof coords === 'string') {
      try {
        coords = JSON.parse(coords);
      } catch (e) {
        console.error('Failed to parse coordinates for zone:', zone.zone_code, coords);
        return;
      }
    }
    
    const colors = zoneColors[zone.zone_type] || zoneColors.room;
    const isSelected = selectedZone?.id === zone.id;
    
    ctx.fillStyle = colors.fill;
    ctx.strokeStyle = colors.stroke;
    ctx.globalAlpha = isSelected ? 0.7 : 0.4;
    ctx.lineWidth = isSelected ? 3 : 2;
    
    if (coords.type === 'rectangle') {
      ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
      ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
    } else if (coords.type === 'circle') {
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, coords.radius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    } else if (coords.type === 'polygon' && coords.points) {
      ctx.beginPath();
      ctx.moveTo(coords.points[0].x, coords.points[0].y);
      coords.points.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    
    // Draw zone label
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    const centerX = coords.center_point?.x || coords.x + (coords.width || 0) / 2;
    const centerY = coords.center_point?.y || coords.y + (coords.height || 0) / 2;
    
    ctx.fillText(zone.zone_code, centerX, centerY - 5);
    ctx.font = '10px Arial';
    ctx.fillText(zone.zone_name, centerX, centerY + 8);
  };

  // Draw entity
  const drawEntity = (ctx, entity) => {
    console.log('Drawing entity:', entity);
    
    const x = entity.current_x || entity.x || 0;
    const y = entity.current_y || entity.y || 0;
    const color = entityColors[entity.entity_type] || '#FF5722';
    
    // Skip if no valid position
    if (x === 0 && y === 0) {
      console.log('Skipping entity with no position:', entity);
      return;
    }
    
    // Draw entity circle
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    ctx.arc(x, y, 8, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw entity border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Draw entity label
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    const label = entity.entity_id || entity.display_name || entity.name || 'Unknown';
    ctx.fillText(label.substring(0, 8), x, y - 12);
    
    // Draw status indicator if needed
    if (entity.status === 'alert') {
      ctx.fillStyle = '#ff0000';
      ctx.beginPath();
      ctx.arc(x + 6, y - 6, 3, 0, 2 * Math.PI);
      ctx.fill();
    }
  };

  // Draw gateway
  const drawGateway = (ctx, gateway) => {
    console.log('Drawing gateway:', gateway);
    const x = gateway.x || 0;
    const y = gateway.y || 0;
    const isSelected = selectedGateway?.id === gateway.id;
    const size = 12;
    const radius = 2;
    
    // Show unpositioned gateways at top-left corner with special indicator
    const isUnpositioned = (x === 0 || x === null || x === undefined) && (y === 0 || y === null || y === undefined);
    const displayX = isUnpositioned ? 50 : x;
    const displayY = isUnpositioned ? 50 : y;
    
    console.log(`Gateway ${gateway.name}: x=${x}, y=${y}, isUnpositioned=${isUnpositioned}, displayX=${displayX}, displayY=${displayY}`);
    
    // Draw gateway rounded rectangle (like legend)
    ctx.fillStyle = isUnpositioned ? '#FFA726' : (gateway.status === 'online' ? '#2196F3' : '#F44336');
    ctx.globalAlpha = isSelected ? 0.9 : 0.8;
    
    // Draw rounded rectangle manually for compatibility
    const rectX = displayX - size/2;
    const rectY = displayY - size/2;
    
    ctx.beginPath();
    ctx.moveTo(rectX + radius, rectY);
    ctx.lineTo(rectX + size - radius, rectY);
    ctx.quadraticCurveTo(rectX + size, rectY, rectX + size, rectY + radius);
    ctx.lineTo(rectX + size, rectY + size - radius);
    ctx.quadraticCurveTo(rectX + size, rectY + size, rectX + size - radius, rectY + size);
    ctx.lineTo(rectX + radius, rectY + size);
    ctx.quadraticCurveTo(rectX, rectY + size, rectX, rectY + size - radius);
    ctx.lineTo(rectX, rectY + radius);
    ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
    ctx.closePath();
    ctx.fill();
    
    // Draw gateway border
    ctx.strokeStyle = isSelected ? '#000' : '#fff';
    ctx.lineWidth = isSelected ? 2 : 1;
    ctx.stroke();
    
    // Draw gateway label
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    const label = isUnpositioned ? `${gateway.name} (não posicionado)` : gateway.name;
    ctx.fillText(label, displayX, displayY - 10);
  };

  // Draw current drawing path
  const drawCurrentPath = (ctx) => {
    if (currentPath.length === 0) return;
    
    ctx.strokeStyle = '#2196F3';
    ctx.fillStyle = 'rgba(33, 150, 243, 0.2)';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    if (drawingMode === 'rectangle' && currentPath.length === 2) {
      const [start, end] = currentPath;
      const width = end.x - start.x;
      const height = end.y - start.y;
      ctx.fillRect(start.x, start.y, width, height);
      ctx.strokeRect(start.x, start.y, width, height);
    } else if (drawingMode === 'circle' && currentPath.length === 2) {
      const [center, edge] = currentPath;
      const radius = Math.sqrt((edge.x - center.x) ** 2 + (edge.y - center.y) ** 2);
      ctx.beginPath();
      ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    } else if (drawingMode === 'polygon' && currentPath.length > 0) {
      ctx.beginPath();
      ctx.moveTo(currentPath[0].x, currentPath[0].y);
      currentPath.forEach(point => ctx.lineTo(point.x, point.y));
      if (currentPath.length > 2) {
        ctx.lineTo(mousePos.x, mousePos.y);
        ctx.closePath();
        ctx.fill();
      }
      ctx.stroke();
    }
    
    ctx.setLineDash([]);
  };

  // Mouse event handlers
  const getCanvasCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate actual canvas coordinates considering CSS scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  };

  const handleMouseDown = (event) => {
    const coords = getCanvasCoordinates(event);
    
    if (isPositioningGateway) {
      // Check if clicked on a gateway first
      const clickedGateway = findGatewayAtPoint(coords.x, coords.y);
      if (clickedGateway) {
        onGatewayClick(clickedGateway);
        return;
      }
      // If in positioning mode and a gateway is selected, position it
      if (selectedGateway) {
        onGatewayPosition(coords.x, coords.y);
        return;
      }
    }
    
    if (drawingMode === 'select') {
      const clickedZone = findZoneAtPoint(coords.x, coords.y);
      onZoneSelect(clickedZone);
    } else if (drawingMode === 'polygon') {
      // Polígono usa cliques, não drag
      if (!isDrawing) {
        console.log('Starting polygon at:', coords);
        setIsDrawing(true);
        setCurrentPath([coords]);
      } else {
        console.log('Adding polygon point:', coords);
        addPolygonPoint(coords);
      }
    } else {
      // Retângulo e círculo usam drag
      setIsDrawing(true);
      setCurrentPath([coords]);
    }
  };

  const handleMouseMove = (event) => {
    const coords = getCanvasCoordinates(event);
    setMousePos(coords);
    
    if (isDrawing && drawingMode !== 'polygon') {
      if (drawingMode === 'rectangle' || drawingMode === 'circle') {
        setCurrentPath(prev => [prev[0], coords]);
      }
    }
  };

  const handleMouseUp = (event) => {
    if (!isDrawing || drawingMode === 'polygon') return;
    
    if (drawingMode === 'rectangle') {
      finishRectangle();
    } else if (drawingMode === 'circle') {
      finishCircle();
    }
  };

  const handleDoubleClick = (event) => {
    if (drawingMode === 'polygon' && isDrawing) {
      finishPolygon();
    }
  };

  // Finish drawing functions
  const finishRectangle = () => {
    if (currentPath.length === 2) {
      const [start, end] = currentPath;
      const zoneData = {
        coordinates: {
          type: 'rectangle',
          x: Math.min(start.x, end.x),
          y: Math.min(start.y, end.y),
          width: Math.abs(end.x - start.x),
          height: Math.abs(end.y - start.y),
          center_point: {
            x: (start.x + end.x) / 2,
            y: (start.y + end.y) / 2
          }
        }
      };
      console.log('MapCanvas: Creating zone with data:', zoneData);
      onZoneCreate(zoneData);
    }
    resetDrawing();
  };

  const finishCircle = () => {
    if (currentPath.length === 2) {
      const [center, edge] = currentPath;
      const radius = Math.sqrt((edge.x - center.x) ** 2 + (edge.y - center.y) ** 2);
      const zoneData = {
        coordinates: {
          type: 'circle',
          x: center.x,
          y: center.y,
          radius: radius,
          center_point: center
        }
      };
      onZoneCreate(zoneData);
    }
    resetDrawing();
  };

  const addPolygonPoint = (coords) => {
    setCurrentPath(prev => {
      const newPath = [...prev, coords];
      console.log('Polygon points:', newPath.length);
      return newPath;
    });
  };

  const finishPolygon = () => {
    if (currentPath.length >= 3) {
      const zoneData = {
        coordinates: {
          type: 'polygon',
          points: currentPath,
          center_point: calculatePolygonCenter(currentPath)
        }
      };
      onZoneCreate(zoneData);
    }
    resetDrawing();
  };

  const calculatePolygonCenter = (points) => {
    const x = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const y = points.reduce((sum, p) => sum + p.y, 0) / points.length;
    return { x, y };
  };

  const resetDrawing = () => {
    setIsDrawing(false);
    setCurrentPath([]);
    onDrawingModeChange('select');
  };

  // Find zone at point
  const findZoneAtPoint = (x, y) => {
    return zones.find(zone => {
      const coords = zone.coordinates;
      if (!coords) return false;
      
      if (coords.type === 'rectangle') {
        return x >= coords.x && x <= coords.x + coords.width &&
               y >= coords.y && y <= coords.y + coords.height;
      } else if (coords.type === 'circle') {
        const distance = Math.sqrt((x - coords.x) ** 2 + (y - coords.y) ** 2);
        return distance <= coords.radius;
      } else if (coords.type === 'polygon' && coords.points) {
        return isPointInPolygon({ x, y }, coords.points);
      }
      return false;
    });
  };

  // Find gateway at point
  const findGatewayAtPoint = (x, y) => {
    return gateways.find(gateway => {
      const isUnpositioned = (gateway.x === 0 || !gateway.x) && (gateway.y === 0 || !gateway.y);
      const gx = isUnpositioned ? 50 : (gateway.x || 0);
      const gy = isUnpositioned ? 50 : (gateway.y || 0);
      const hitSize = 12;
      const hit = x >= gx - hitSize && x <= gx + hitSize && y >= gy - hitSize && y <= gy + hitSize;
      if (hit) console.log('Gateway hit:', gateway.name, 'at', gx, gy);
      return hit;
    });
  };

  // Point in polygon algorithm
  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      if (((polygon[i].y > point.y) !== (polygon[j].y > point.y)) &&
          (point.x < (polygon[j].x - polygon[i].x) * (point.y - polygon[i].y) / (polygon[j].y - polygon[i].y) + polygon[i].x)) {
        inside = !inside;
      }
    }
    return inside;
  };

  // Redraw canvas when data changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Initial canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      // Force initial draw
      setTimeout(() => drawCanvas(), 100);
    }
  }, []);

  // Show drawing tools only when editing a zone or creating a new one
  const showDrawingTools = selectedZone || drawingMode !== 'select';

  return (
    <Box>
      
      {/* Drawing Tools - Only show when editing or creating */}
      {showDrawingTools && (
        <Paper sx={{ p: 1, mb: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Ferramentas de Desenho (Modo: {drawingMode})
          </Typography>
          <ButtonGroup size="small" sx={{ mr: 2 }}>
            <Button
              variant={drawingMode === 'rectangle' ? 'contained' : 'outlined'}
              onClick={() => onDrawingModeChange('rectangle')}
            >
              Retângulo
            </Button>
            <Button
              variant={drawingMode === 'circle' ? 'contained' : 'outlined'}
              onClick={() => onDrawingModeChange('circle')}
            >
              Círculo
            </Button>
            <Button
              variant={drawingMode === 'polygon' ? 'contained' : 'outlined'}
              onClick={() => onDrawingModeChange('polygon')}
            >
              Polígono
            </Button>
          </ButtonGroup>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              resetDrawing();
              onZoneSelect(null);
            }}
          >
            Cancelar
          </Button>
        </Paper>
      )}
      
      {/* Entity Legend - Always visible */}
      <Paper sx={{ p: 1, mb: 2 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
          Legenda
        </Typography>
        <Chip
          icon={<Box sx={{ width: 12, height: 12, bgcolor: entityColors.equipment, borderRadius: '50%' }} />}
          label="Equipamentos"
          size="small"
          sx={{ mr: 1 }}
        />
        <Chip
          icon={<Box sx={{ width: 12, height: 12, bgcolor: entityColors.patient, borderRadius: '50%' }} />}
          label="Pacientes"
          size="small"
          sx={{ mr: 1 }}
        />
        <Chip
          icon={<Box sx={{ width: 12, height: 12, bgcolor: entityColors.staff, borderRadius: '50%' }} />}
          label="Funcionários"
          size="small"
          sx={{ mr: 1 }}
        />
        <Chip
          icon={<Box sx={{ width: 12, height: 12, bgcolor: '#2196F3', borderRadius: '20%' }} />}
          label="Gateways"
          size="small"
        />
      </Paper>
      
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          cursor: isPositioningGateway ? 'crosshair' : (drawingMode === 'select' ? 'pointer' : 'crosshair'),
          width: '100%',
          height: 'auto',
          maxWidth: '800px'
        }}
      />
      
      {/* Instructions */}
      {isDrawing && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
          {drawingMode === 'polygon' && (
            <Typography variant="caption">
              Clique para adicionar pontos. Duplo clique para finalizar.
            </Typography>
          )}
          {(drawingMode === 'rectangle' || drawingMode === 'circle') && (
            <Typography variant="caption">
              Clique e arraste para desenhar. Solte para finalizar.
            </Typography>
          )}
        </Box>
      )}
      
      {isPositioningGateway && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="caption">
            {selectedGateway 
              ? `Clique no mapa para posicionar ${selectedGateway.name}` 
              : 'Clique em um gateway para selecioná-lo, depois clique no mapa para posicioná-lo'
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MapCanvas;